"use client";

import { c as _c } from "react/compiler-runtime";
import { MinimalWave } from "@/contexts/wave/hooks/useEnhancedWavesList";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Link from "next/link";
import React, { useEffect, useEffectEvent, useRef } from "react";
import UnifiedWavesListEmpty from "./UnifiedWavesListEmpty";
import { UnifiedWavesListLoader } from "./UnifiedWavesListLoader";
import UnifiedWavesListWaves, { UnifiedWavesListWavesHandle } from "./UnifiedWavesListWaves";
interface UnifiedWavesListProps {
  readonly waves: MinimalWave[];
  readonly fetchNextPage: () => void;
  readonly hasNextPage: boolean | undefined;
  readonly isFetching: boolean;
  readonly isFetchingNextPage: boolean;
  readonly onHover: (waveId: string) => void;
  readonly scrollContainerRef: React.RefObject<HTMLDivElement | null>;
}
const UnifiedWavesList: React.FC<UnifiedWavesListProps> = t0 => {
  const $ = _c(33);
  const {
    waves,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    onHover,
    scrollContainerRef
  } = t0;
  const {
    isApp
  } = useDeviceInfo();
  const listRef = useRef(null);
  const hasFetchedRef = useRef(false);
  let t1;
  if ($[0] !== fetchNextPage) {
    t1 = () => {
      hasFetchedRef.current = true;
      fetchNextPage();
    };
    $[0] = fetchNextPage;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const triggerFetchNextPage = useEffectEvent(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      hasFetchedRef.current = false;
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== hasNextPage || $[4] !== isFetchingNextPage) {
    t3 = [hasNextPage, isFetchingNextPage];
    $[3] = hasNextPage;
    $[4] = isFetchingNextPage;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] !== hasNextPage || $[7] !== isFetchingNextPage || $[8] !== triggerFetchNextPage) {
    t4 = () => {
      const sentinel = listRef.current?.sentinelRef.current;
      if (!sentinel || !hasNextPage || isFetchingNextPage) {
        return;
      }
      const cb = entries => {
        const [entry] = entries;
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage && !hasFetchedRef.current) {
          triggerFetchNextPage();
        }
      };
      const obs = new IntersectionObserver(cb, {
        root: listRef.current?.containerRef.current,
        rootMargin: "100px"
      });
      obs.observe(sentinel);
      return () => obs.disconnect();
    };
    $[6] = hasNextPage;
    $[7] = isFetchingNextPage;
    $[8] = triggerFetchNextPage;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== hasNextPage || $[11] !== isFetchingNextPage) {
    t5 = [hasNextPage, isFetchingNextPage];
    $[10] = hasNextPage;
    $[11] = isFetchingNextPage;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  useEffect(t4, t5);
  let t6;
  if ($[13] !== isApp) {
    t6 = !isApp && <div className="tw-px-4 tw-mb-4 tw-w-full"><Link href="/waves?create=wave" className="tw-no-underline tw-ring-1 tw-ring-inset tw-ring-iron-700 desktop-hover:hover:tw-ring-iron-700 tw-text-iron-300 tw-flex tw-items-center tw-justify-center tw-gap-x-2 tw-rounded-lg tw-py-2 tw-px-4 tw-text-xs tw-bg-iron-800 desktop-hover:hover:tw-text-primary-400 tw-font-semibold tw-transition-all tw-duration-300"><FontAwesomeIcon icon={faPlus} className="tw-size-3.5 -tw-ml-1.5 tw-flex-shrink-0" /><span className="tw-text-xs tw-font-semibold">Create Wave</span></Link></div>;
    $[13] = isApp;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== onHover || $[16] !== scrollContainerRef || $[17] !== waves) {
    t7 = <UnifiedWavesListWaves ref={listRef} waves={waves} onHover={onHover} scrollContainerRef={scrollContainerRef} />;
    $[15] = onHover;
    $[16] = scrollContainerRef;
    $[17] = waves;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const t8 = isFetching && waves.length === 0;
  let t9;
  if ($[19] !== isFetchingNextPage || $[20] !== t8) {
    t9 = <UnifiedWavesListLoader isFetching={t8} isFetchingNextPage={isFetchingNextPage} />;
    $[19] = isFetchingNextPage;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== isFetching || $[23] !== isFetchingNextPage || $[24] !== waves) {
    t10 = <UnifiedWavesListEmpty sortedWaves={waves} isFetching={isFetching} isFetchingNextPage={isFetchingNextPage} />;
    $[22] = isFetching;
    $[23] = isFetchingNextPage;
    $[24] = waves;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t10 || $[27] !== t7 || $[28] !== t9) {
    t11 = <div className="tw-w-full">{t7}{t9}{t10}</div>;
    $[26] = t10;
    $[27] = t7;
    $[28] = t9;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== t11 || $[31] !== t6) {
    t12 = <div className="tw-mb-4"><div className="tw-h-full tw-bg-iron-950 tw-rounded-xl tw-ring-1 tw-ring-inset tw-ring-iron-800 tw-py-4">{t6}{t11}</div></div>;
    $[30] = t11;
    $[31] = t6;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  return t12;
};
export default UnifiedWavesList;